import React, { useCallback } from 'react';
import { SliderConfig } from '../../types/calculator';
import { formatCurrency, formatPercent, formatNumber } from '../../utils/formatters';

interface SliderInputProps {
  config: SliderConfig;
  value: number;
  onChange: (value: number) => void;
}

export const SliderInput: React.FC<SliderInputProps> = ({ config, value, onChange }) => {
  const { min, max, step, label, helperText, format } = config;

  // Format the display value
  const formatValue = useCallback((val: number): string => {
    switch (format) {
      case 'currency':
        return formatCurrency(val);
      case 'percent':
        return formatPercent(val);
      case 'decimal':
        return formatNumber(val, 1);
      default:
        return formatNumber(val, 0);
    }
  }, [format]);

  // Parse input value
  const parseValue = useCallback((input: string): number => {
    const cleaned = input.replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed)) return value;
    return Math.min(max, Math.max(min, parsed));
  }, [min, max, value]);

  // Handle slider change
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(e.target.value));
  };

  // Handle direct input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseValue(e.target.value);
    onChange(newValue);
  };

  // Calculate slider progress for visual feedback
  const progress = ((value - min) / (max - min)) * 100;

  // Calculate midpoint value
  const midValue = (min + max) / 2;

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-semibold text-[#6F8D90]">
          {label}
        </label>
        <div className="relative">
          <input
            type="text"
            value={formatValue(value)}
            onChange={handleInputChange}
            className="w-[100px] text-right text-sm font-bold text-orange bg-white border border-orange rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent"
            aria-label={`${label} value`}
          />
        </div>
      </div>

      {helperText && (
        <p className="text-xs text-[#A2B7BB] mb-3">{helperText}</p>
      )}

      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleSliderChange}
          className="w-full cursor-pointer"
          aria-label={label}
          style={{
            background: `linear-gradient(to right, #C35E38 0%, #823243 ${progress}%, #E5E7EB ${progress}%, #E5E7EB 100%)`,
          }}
        />
        {/* Midpoint marker */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-3 bg-gray-300 pointer-events-none" />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{formatValue(min)}</span>
          <span className="absolute left-1/2 -translate-x-1/2">{formatValue(midValue)}</span>
          <span>{formatValue(max)}</span>
        </div>
      </div>

      {format === 'percent' && (
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>Employee Pays</span>
          <span>Split</span>
          <span>Company Pays</span>
        </div>
      )}
    </div>
  );
};
